import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../utils/api';
import './CourseRegister.css';

// Real social proof, sourced from the live site (TrustStrip + Testimonials).
const STATS = [
  { value: '40+', label: 'Years of training & practice' },
  { value: '4000+', label: 'Lives transformed' },
  { value: 'CHI-USA', label: 'Certified trainer' },
];

// The site's own testimonial videos (src/pages/TestimonialsPage.js).
const PROOF_VIDEOS = [
  { src: '/assets/testimonials/trainees/trainee-4.mp4', label: 'Trainee story' },
  { src: '/assets/testimonials/testimonial-v12.mp4', label: 'Client story' },
];

function ProofVideo({ src, label }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  return (
    <button type="button" className="creg__video" onClick={toggle} aria-label={`Play ${label}`}>
      <video ref={ref} src={src} playsInline preload="metadata" onEnded={() => setPlaying(false)} />
      {!playing && (
        <span className="creg__video-overlay">
          <span className="creg__video-play">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><polygon points="6,3 20,12 6,21" /></svg>
          </span>
          <span className="creg__video-label">{label}</span>
        </span>
      )}
    </button>
  );
}

// Lightweight, mobile-first registration / lead-capture landing page for a single course.
// Route: /register/:slug  (built for clinical-hypnotherapy-level-1).
// Pulls title/subtitle/instructor from the courses API but keeps the on-page
// detail intentionally minimal — the job of this page is to register people.

// Per-level copy keyed by course slug — so /register/clinical-hypnotherapy-level-{1,2,3}
// each show the right fallback title/subtitle + factual highlight chips (from schema.sql).
// The course title/subtitle/description/chapters still come from the API when available;
// this is the offline fallback plus the short highlight chips the API doesn't carry.
const LEVELS = {
  'clinical-hypnotherapy-level-1': {
    title: 'Clinical Hypnotherapy — Level I',
    subtitle: 'Master Hypnotist · CHI-USA Certified',
    highlights: ['20 hours', 'Master Hypnotist certificate', 'Beginner friendly'],
  },
  'clinical-hypnotherapy-level-2': {
    title: 'Clinical Hypnotherapy — Level II',
    subtitle: 'Master Hypnotherapist · CHI-USA Certified',
    highlights: ['40 hours', 'Master Hypnotherapist certificate', 'Level I prerequisite'],
  },
  'clinical-hypnotherapy-level-3': {
    title: 'Clinical Hypnotherapy — Level III',
    subtitle: 'Clinical Hypnotherapist · CHI-USA Certified',
    highlights: ['50 hours', 'Clinical Hypnotherapist certificate', 'Level II prerequisite'],
  },
};
const DEFAULT_LEVEL = LEVELS['clinical-hypnotherapy-level-1'];

// The trainer, sourced verbatim from her profile at /cons/dr-manju-agrawal.
const TRAINER = {
  name: 'Dr. Manju Agrawal',
  title: 'Professor Emeritus & Founder Director',
  tagline: 'Amity University · Mind Spa Foundation',
  image: '/assets/gallery/named/gallery-3.webp',
  initials: 'MA',
  profileUrl: '/cons/dr-manju-agrawal',
  summary: 'Professor Emeritus at Amity University with 40+ years of experience. Internationally certified hypnotherapy trainer, author, and founder of MindSpa Foundation.',
  tags: ['40+ yrs Experience', 'CHI Certified Trainer', 'Ph.D. Psychology'],
  sections: [
    { heading: 'Academic & Professional Roles', body: 'Currently Professor Emeritus at Amity University with more than 40 years of experience as Professor of Psychology and social entrepreneur. Former Dean of Student Welfare. Founder Director of the Behavioural Science Institute, the Centre of Excellence for Happiness at Amity University, and the Mind Spa Foundation.' },
    { heading: 'Expertise & Certifications', body: 'Ph.D. in Psychology · Subconscious mind coach · Internationally certified (IMDHA) hypnotherapy practitioner · CHI certified hypnotherapy trainer · Past-life regression therapist · Family constellation therapist · Training in energy medicine, NLP, EFT, CBT, mindfulness, chakra diagnosis & healing, and various psychotherapies.' },
    { heading: 'Publications & Recognition', body: 'International speaker and trainer · Author of 3 books and two psychometric scales · Many papers in Scopus-indexed journals of USA, UK, and India. Her latest book "Psychology of Self-talk" is transformative. Next book on Meditative Self-talk Therapy is under publication.' },
    { heading: 'Fellowships & Awards', body: 'Recipient of UGC, ICSSR, ICMR, Royal Netherlands, Bill Gates, and EXXON Mobil (USA) fellowships · Part of Indian and international delegations to the United Nations · ICPD Advancement Award (USAID, USA) · Four outstanding national and international woman achiever awards · Former executive member of the Planning Commission on WCD for the 11th Plan.' },
    { heading: 'Social Impact & Coaching', body: 'Intensively worked for marginalised women in 20 districts of UP. As an executive coach, she has helped many corporate employees succeed 10X and fulfil their dreams. She has a record of bringing out people from emotional dysregulation, panic attacks, untreatable diseases, allergies, anxieties, fears, depression, fainting, relationship breakups, and addictions in just a few sessions — a no-medicine approach with only positive side-effects and outcomes.' },
  ],
  quotes: [
    { quote: "Had a wonderful hypnotherapy session at Mind Spa Clinic. Manju Ma'am helped me find closure for past traumatic events I'd been holding onto for years.", author: 'Parastish Dubey' },
    { quote: 'Sessions with Manju ma’am are MAGICAL and I want to say in one word "AN ANGEL" on a mission to heal if you really need it.', author: 'Smriti Srivastava' },
  ],
};

function CourseRegister() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const level = LEVELS[slug] || DEFAULT_LEVEL;

  useEffect(() => {
    let active = true;
    api.getCourseBySlug(slug)
      .then((data) => { if (active) setCourse(data); })
      .catch(() => { /* fall back to static copy below */ });
    document.title = `Register · ${level.title} · Mind Spa`;
    return () => { active = false; };
  }, [slug, level.title]);

  const title = course?.title || level.title;
  const subtitle = course?.subtitle || level.subtitle;
  const instructor = TRAINER;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please fill in your name, email and phone.');
      return;
    }
    setSubmitting(true);
    try {
      await api.registerForCourse({ course_slug: slug, ...form });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="creg">
      <div className="creg__card">
        {/* Top bar: logo left, home button right */}
        <div className="creg__topbar">
          <Link to="/" className="creg__logo" aria-label="Mind Spa — home">
            <img src="/logo.png" alt="" className="creg__logo-img" width="1063" height="1152" decoding="async" />
            <span className="creg__logo-brand">Mind Spa</span>
          </Link>
          <Link to="/" className="creg__home-btn">
            <span>Home</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="creg__brand">
          <span className="creg__eyebrow">Registration</span>
        </div>

        {done ? (
          <div className="creg__done">
            <div className="creg__tick" aria-hidden="true">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h1 className="creg__done-title">You're registered!</h1>
            <p className="creg__done-text">
              Thanks, {form.name.split(' ')[0] || 'there'} — your spot for <strong>{title}</strong> is
              saved. We'll email <strong>{form.email}</strong> with the joining details shortly.
            </p>
            <Link to="/" className="btn-outline creg__home">Back to home</Link>
          </div>
        ) : (
          <>
            {/* Minimal course summary */}
            <header className="creg__head">
              <h1 className="creg__title">{title}</h1>
              <p className="creg__subtitle">{subtitle}</p>

              <div className="creg__instructor">
                <div className="creg__avatar">
                  {instructor.image
                    ? <img src={instructor.image} alt={instructor.name} loading="lazy" />
                    : <span>{instructor.initials || 'MA'}</span>}
                </div>
                <div className="creg__instructor-meta">
                  <span className="creg__instructor-label">Led by</span>
                  <span className="creg__instructor-name">{instructor.name}</span>
                  <div className="creg__instructor-chips">
                    {instructor.tags.map((t) => (
                      <span key={t} className="creg__instructor-chip">{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              <ul className="creg__highlights">
                {level.highlights.map((h) => (
                  <li key={h} className="creg__chip">{h}</li>
                ))}
              </ul>
            </header>

            {/* Registration form */}
            <form className="creg__form" onSubmit={handleSubmit} noValidate>
              {error && <div className="creg__error" role="alert">{error}</div>}

              <div className="creg__field">
                <label htmlFor="cr-name">Full name</label>
                <input id="cr-name" name="name" value={form.name} onChange={handleChange}
                       autoComplete="name" maxLength={120} placeholder="Your name" required />
              </div>

              <div className="creg__field">
                <label htmlFor="cr-email">Email</label>
                <input id="cr-email" name="email" type="email" value={form.email} onChange={handleChange}
                       autoComplete="email" maxLength={190} placeholder="you@example.com" required />
              </div>

              <div className="creg__field">
                <label htmlFor="cr-phone">Phone / WhatsApp</label>
                <input id="cr-phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
                       autoComplete="tel" maxLength={40} placeholder="+91…" required />
              </div>

              <div className="creg__field">
                <label htmlFor="cr-message">Anything you'd like us to know? <span>(optional)</span></label>
                <textarea id="cr-message" name="message" value={form.message} onChange={handleChange}
                          maxLength={4000} placeholder="Your background, questions, preferred batch…" />
              </div>

              <button type="submit" className="btn-primary creg__submit" disabled={submitting}>
                {submitting ? 'Registering…' : 'Register your interest'}
              </button>

              <p className="creg__fineprint">
                We'll reach out with the schedule, fees and joining details.
              </p>
            </form>

            {/* Course details — kept at the bottom, below the form */}
            <section className="creg__details">
              <h2 className="creg__details-title">About this course</h2>
              {course?.description ? (
                <div className="creg__details-body"
                     dangerouslySetInnerHTML={{ __html: course.description }} />
              ) : (
                <p className="creg__details-body">
                  A professional module in the CHI-USA certified clinical hypnotherapy
                  track — covering the principles of hypnosis and the core techniques used
                  to guide and deepen the healing process. Register your interest to get
                  the full curriculum, schedule and joining details.
                </p>
              )}

              {course?.chapters && course.chapters.length > 0 && (
                <>
                  <h3 className="creg__details-subtitle">What you'll cover</h3>
                  <ol className="creg__chapters">
                    {course.chapters.map((ch, i) => (
                      <li key={ch.id || i} className="creg__chapter">
                        <span className="creg__chapter-num">{String(i + 1).padStart(2, '0')}</span>
                        <span>{ch.title}</span>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </section>

            {/* Meet your trainer — sourced from /cons/dr-manju-agrawal */}
            <section className="creg__trainer">
              <h2 className="creg__details-title">Meet your trainer</h2>
              <div className="creg__trainer-head">
                <div className="creg__avatar creg__avatar--lg">
                  <img src={instructor.image} alt={instructor.name} loading="lazy" />
                </div>
                <div>
                  <div className="creg__instructor-name">{instructor.name}</div>
                  <div className="creg__trainer-title">{instructor.title}</div>
                  <div className="creg__trainer-tagline">{instructor.tagline}</div>
                </div>
              </div>
              <p className="creg__trainer-summary">{instructor.summary}</p>
              <div className="creg__trainer-sections">
                {instructor.sections.map((s) => (
                  <div key={s.heading} className="creg__trainer-section">
                    <h3 className="creg__details-subtitle">{s.heading}</h3>
                    <p className="creg__details-body">{s.body}</p>
                  </div>
                ))}
              </div>
              <Link to={instructor.profileUrl} className="creg__proof-link">View full profile &rarr;</Link>
            </section>

            {/* Social proof — real stats, written client quotes + the site's videos */}
            <section className="creg__proof">
              <div className="creg__stats">
                {STATS.map((s) => (
                  <div key={s.label} className="creg__stat">
                    <span className="creg__stat-value">{s.value}</span>
                    <span className="creg__stat-label">{s.label}</span>
                  </div>
                ))}
              </div>

              <h2 className="creg__proof-title">In their own words</h2>

              <div className="creg__quotes">
                {TRAINER.quotes.map((q) => (
                  <figure key={q.author} className="creg__quote">
                    <blockquote>&ldquo;{q.quote}&rdquo;</blockquote>
                    <figcaption>— {q.author}</figcaption>
                  </figure>
                ))}
              </div>

              <p className="creg__proof-lead">
                Real stories from clients and trainees of Mind Spa — watch what the
                work looks like, in their own words.
              </p>
              <div className="creg__videos">
                {PROOF_VIDEOS.map((v) => <ProofVideo key={v.src} {...v} />)}
              </div>
              <Link to="/testimonials" className="creg__proof-link">Watch more stories &rarr;</Link>
            </section>
          </>
        )}

        <footer className="creg__footer">
          <Link to="/" className="creg__footer-link">MindSpaIndia 2026</Link>
        </footer>
      </div>
    </div>
  );
}

export default CourseRegister;
